import { useTranslation } from 'react-i18next'
import { Box, Typography, useTheme } from '@mui/material'
import { Chart } from 'react-chartjs-2'
import { DetailedGapDescription } from '../../shared/model/models'
import { DiversityDimenstionConfig, ModuleConfig } from '../../shared/model'
import { translatedDomainName } from '../../shared/utils'

type FsDetailedDescriptionCausesProps = {
  gapDetailed: DetailedGapDescription
  moduleConfig?: ModuleConfig
  diversityDimenstionConfig?: DiversityDimenstionConfig
}

const FsDescriptionByCauses = (props: FsDetailedDescriptionCausesProps) => {
  const { t, i18n } = useTranslation()
  const { gapDetailed, moduleConfig, diversityDimenstionConfig } = props
  const theme = useTheme()
  const dependentCharacteristic = gapDetailed.referenceGroup.descriptions.map(
    (ele) => ele.descriptionDependentVariable.characteristic.name,
  )
  const referenceGrpName = translatedDomainName(
    `${gapDetailed.referenceGroup.groupVariable.label}`,
    t,
  )
  const grpOfInterestGrpName = translatedDomainName(
    `${gapDetailed.groupOfInterest.groupVariable.label}`,
    t,
  )
  const descriptiveNames = [...gapDetailed.referenceGroup.descriptions].map(
    (ele) =>
      translatedDomainName(`${ele.descriptiveVariable.label}`, t).charAt(0).toUpperCase() +
      translatedDomainName(`${ele.descriptiveVariable.label}`, t).slice(1),
  )
  const referenceGrpMean = [...gapDetailed.referenceGroup.descriptions].map(
    (ele) =>
      `${Number(ele.descriptionDependentVariable.mean).toFixed(moduleConfig?.numberFractionDigit)}`,
  )
  const grpOfInterestMean = [...gapDetailed.groupOfInterest.descriptions].map(
    (ele) =>
      `${Number(ele.descriptionDependentVariable.mean).toFixed(moduleConfig?.numberFractionDigit)}`,
  )
  const helpTextHtml =
    gapDetailed && gapDetailed.gapCauseDescriptions.filter((info) => info.locale === i18n.language)
      ? gapDetailed.gapCauseDescriptions.filter((info) => info.locale === i18n.language)[0]
          .gapEmpiricalCauses
      : ''

  return (
    <div
      style={{
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Box py={2} height={descriptiveNames.length * 150} minHeight={500} maxHeight={1200}>
        <Chart
          type="bar"
          id={`description_causes_bar_chart_${moduleConfig?.module}`}
          data={{
            labels: descriptiveNames,
            datasets: [
              {
                label: grpOfInterestGrpName,
                backgroundColor: diversityDimenstionConfig?.fsColor?.primary,
                data: grpOfInterestMean,
                borderRadius: 8,
              },
              {
                label: referenceGrpName,
                backgroundColor: theme.palette.divider,
                data: referenceGrpMean,
                borderRadius: 8,
              },
            ],
          }}
          options={{
            locale: i18n.language,
            maintainAspectRatio: false,
            indexAxis: 'y',
            layout: {
              padding: {
                left: 20,
                right: 120,
              },
            },
            plugins: {
              legend: {
                position: 'top',
                onHover: (event) => {
                  // @ts-ignore
                  if (event.native?.target) event.native.target.style.cursor = 'pointer'
                },
                title: {
                  display: true,
                  text: t(
                    `context.model.name.${
                      gapDetailed?.groupOfInterest.groupVariable.name.split('@')[0]
                    }` as any,
                  ),
                  font: {
                    weight: 'bold',
                  },
                },
              },
              tooltip: {
                callbacks: {
                  label: (context) =>
                    `${context.dataset.label}: ${Number(context.raw).toLocaleString(i18n.language, {
                      maximumFractionDigits: moduleConfig?.numberFractionDigit,
                      minimumFractionDigits: moduleConfig?.numberFractionDigit,
                    })} `.concat(t(`${moduleConfig?.unit}` as any)),
                },
              },
              datalabels: {
                anchor: 'end',
                align: 'end',
                color: theme.palette.text.secondary,
                font: (context) => {
                  const { width } = context.chart
                  const size = Math.round(width / 42)
                  return {
                    size: size < 14 ? size : 14,
                  }
                },
                formatter: (value) =>
                  value && !Number.isNaN(Number(value))
                    ? `${Number(value).toLocaleString(i18n.language, {
                        maximumFractionDigits: moduleConfig?.numberFractionDigit,
                        minimumFractionDigits: moduleConfig?.numberFractionDigit,
                      })} `.concat(t(`${moduleConfig?.unit}` as any))
                    : '',
              },
            },
            scales: {
              x: {
                grid: {
                  color: theme.palette.divider,
                },
                title: {
                  display: true,
                  text: t(`dependentCharacteristic.name.${dependentCharacteristic[0]}` as any),
                  padding: 20,
                  font: {
                    weight: 'bold',
                  },
                },
                border: {
                  display: false,
                },
                ticks: {
                  autoSkip: true,
                  maxTicksLimit: 8,
                  color: theme.palette.text.primary,
                  font: (context) => {
                    const { width } = context.chart
                    const size = Math.round(width / 42)
                    return {
                      size: size < 14 ? size : 14,
                    }
                  },
                  callback: (value) => `${value} `.concat(t(`${moduleConfig?.unit}` as any)),
                },
              },
              y: {
                grid: {
                  display: false,
                },
                title: {
                  display: true,
                  text: t(
                    `descriptiveCharacteristic.name.${gapDetailed?.groupOfInterest.descriptiveCharacteristic.name}` as any,
                  ),
                  padding: 15,
                  font: {
                    weight: 'bold',
                  },
                },
                border: {
                  color: theme.palette.text.disabled,
                },
                ticks: {
                  color: theme.palette.text.primary,
                  font: (context) => {
                    const { width } = context.chart
                    const size = Math.round(width / 42)
                    return {
                      size: size < 14 ? size : 14,
                    }
                  },
                  callback: (value) =>
                    descriptiveNames[Number(value)]
                      ? descriptiveNames[Number(value)].length > 25
                        ? `${descriptiveNames[Number(value)].slice(0, 25)}...`
                        : `${descriptiveNames[Number(value)]}`
                      : '',
                },
              },
            },
          }}
        />
      </Box>
      <Box>
        <Typography component="div" style={{ whiteSpace: 'pre-line' }}>
          {helpTextHtml}
        </Typography>
      </Box>
    </div>
  )
}

export default FsDescriptionByCauses
